import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';
import { isPresent, isMissing, formatRate, formatChange, sum } from '../utils/format';

// Prime has sat at fed funds + 3.00 since 2008; used only to derive prime when
// the API supplies fed funds but not prime itself.
const PRIME_SPREAD_OVER_FED_FUNDS = 3.0;

const HELOCRates = ({ onBackToHome }) => {
  const [selectedProduct, setSelectedProduct] = useState('heloc');
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Null when the API had no rate for the product. Previously returned 8.25 /
  // 7.85 whenever marketData was absent, so the page showed firm quotes before
  // any request had completed.
  const getCurrentRate = (productType) => {
    if (!marketData) return null;
    const value = productType === 'heloc'
      ? marketData.helocAverage.current
      : marketData.heloanAverage.current;
    return isMissing(value) ? null : value;
  };

  const getPrimeRate = () => {
    const value = marketData?.primeRate?.current;
    return isMissing(value) ? null : value;
  };

  const Unavailable = () => <span className="text-gray-400 font-normal">Not available</span>;

  // HELOC/HELOAN product definitions
  const equityProducts = {
    heloc: {
      name: 'HELOC',
      fullName: 'Home Equity Line of Credit',
      description: 'Revolving credit line secured by home equity',
      currentRate: getCurrentRate('heloc'),
      basedOn: 'Prime Rate + Margin',
      primeRate: getPrimeRate(),
      typicalMargin: 2.75,
      icon: '💳',
      structure: 'Variable Rate',
      drawPeriod: '10 years',
      repaymentPeriod: '20 years',
      benefits: [
        'Access funds as needed during draw period',
        'Interest-only payments during draw period',
        'Only pay interest on amount used',
        'Tax-deductible interest (if used for home improvements)',
        'Flexible repayment options'
      ],
      considerations: [
        'Variable interest rate risk',
        'Payment shock when entering repayment period',
        'Home serves as collateral',
        'Rate can increase with Fed rate hikes',
        'Potential for payment increases'
      ]
    },
    heloan: {
      name: 'HELOAN',
      fullName: 'Home Equity Loan',
      description: 'Fixed-rate lump sum loan secured by home equity',
      currentRate: getCurrentRate('heloan'),
      basedOn: 'Market Rates + Credit Risk',
      primeRate: getPrimeRate(),
      typicalMargin: 2.35,
      icon: '🏦',
      structure: 'Fixed Rate',
      drawPeriod: 'Lump sum at closing',
      repaymentPeriod: '5-30 years',
      benefits: [
        'Fixed interest rate for entire term',
        'Predictable monthly payments',
        'Lump sum funding at closing',
        'No payment shock risk',
        'Tax-deductible interest (if used for home improvements)'
      ],
      considerations: [
        'Must borrow full amount upfront',
        'Pay interest on entire loan amount',
        'Less flexibility than HELOC',
        'Home serves as collateral',
        'Potentially higher rate than HELOC initially'
      ]
    }
  };

  // Fetch market data from the API
  useEffect(() => {
    const fetchMarketData = async () => {
      setLoading(true);
      setError(null);
      try {
        console.log('Fetching live HELOC market data from API...');
        const response = await apiService.getComprehensiveMarketData();
        
        if (response && response.data) {
          const d = response.data;

          // Everything below is either a value the API returned or null. The
          // previous version chained `||` fallbacks for all fifteen fields
          // (prime 8.50, HELOC 8.25, ranges 7.75-9.25, spreads 2.75/2.35/0.40),
          // so the page rendered a complete HELOC market with no live data at
          // all - and `||` also discarded any legitimate reading of 0.
          const value = (...candidates) => {
            for (const candidate of candidates) {
              if (isPresent(candidate)) return Number(candidate);
            }
            return null;
          };

          // Prime is conventionally fed funds + 3.00; derived only when fed
          // funds is actually available.
          const derivedPrime = (fedFunds) => sum(fedFunds, PRIME_SPREAD_OVER_FED_FUNDS);

          setMarketData({
            primeRate: {
              current: value(d.prime_rate, derivedPrime(d.fed_funds_rate)),
              previous: value(d.prime_rate_previous, derivedPrime(d.fed_funds_rate_previous)),
              change: value(d.prime_rate_change),
              lastUpdate: d.timestamp ?? null
            },
            fedFunds: {
              current: value(d.fed_funds_rate),
              previous: value(d.fed_funds_rate_previous),
              change: value(d.fed_funds_rate_change),
              lastUpdate: d.timestamp ?? null
            },
            helocAverage: {
              current: value(d.heloc_rate, d.prime_rate),
              previous: value(d.heloc_rate_previous, d.prime_rate_previous),
              change: value(d.heloc_rate_change),
              range: {
                min: value(d.heloc_min_rate),
                max: value(d.heloc_max_rate)
              }
            },
            heloanAverage: {
              current: value(d.heloan_rate),
              previous: value(d.heloan_rate_previous),
              change: value(d.heloan_rate_change),
              range: {
                min: value(d.heloan_min_rate),
                max: value(d.heloan_max_rate)
              }
            },
            historicalSpreads: {
              helocToPrime: value(d.heloc_prime_spread),
              heloanToPrime: value(d.heloan_prime_spread),
              helocVsHeloan: value(d.heloc_heloan_spread)
            }
          });
        } else {
          throw new Error('Invalid response format from market data API');
        }
      } catch (err) {
        console.error('Failed to fetch HELOC market data:', err);
        setError(`Failed to load HELOC rates data: ${err.message}. Please try refreshing the page.`);
        setMarketData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
  }, []);

  const getChangeColor = (change) => {
    if (isMissing(change)) return 'text-gray-400';
    if (change > 0) return 'text-red-600';
    if (change < 0) return 'text-green-600';
    return 'text-gray-600';
  };

  const selectedProductData = equityProducts[selectedProduct];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-purple-600 font-medium">Loading HELOC/HELOAN data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">⚠️</div>
          <p className="text-red-600 font-medium">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <span className="text-4xl mr-3">🏠</span>
                HELOC & Home Equity Loans
              </h1>
              <p className="text-gray-600 mt-2">
                Compare home equity borrowing options and track Prime Rate movements
              </p>
            </div>
            <button
              onClick={onBackToHome}
              className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Product Selector */}
        <div className="mb-8">
          <div className="flex space-x-4">
            {Object.entries(equityProducts).map(([key, product]) => (
              <button
                key={key}
                onClick={() => setSelectedProduct(key)}
                className={`flex-1 p-4 rounded-xl border-2 transition-all duration-200 ${
                  selectedProduct === key
                    ? 'border-purple-500 bg-purple-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-purple-300 hover:shadow-sm'
                }`}
              >
                <div className="text-center">
                  <div className="text-3xl mb-2">{product.icon}</div>
                  <h3 className="font-bold text-lg text-gray-900">{product.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{product.fullName}</p>
                  <p className="text-sm text-gray-500 mt-1">{product.structure}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Current Market Overview */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Rate Information */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">📊</span>
              Current {selectedProductData.name} Rates
            </h2>
            
            <div className="space-y-4">
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium">Current Rate</span>
                  <span className="text-2xl font-bold text-purple-600">
                    {formatRate(selectedProductData.currentRate)}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Based on {selectedProductData.basedOn}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">Prime Rate</div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatRate(marketData.primeRate.current)}
                  </div>
                  <div className={`text-sm ${getChangeColor(marketData.primeRate.change)}`}>
                    {formatChange(marketData.primeRate.change)}
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-sm text-gray-600">Typical Margin</div>
                  <div className="text-lg font-bold text-gray-900">
                    {formatRate(selectedProductData.typicalMargin)}
                  </div>
                  {/* An illustrative term, not a live reading - labelled so it
                      is not read as a quote alongside the rates beside it. */}
                  <div className="text-sm text-gray-500">Above Prime (illustrative)</div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Features */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <span className="text-2xl mr-2">{selectedProductData.icon}</span>
              {selectedProductData.name} Features
            </h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Structure:</span>
                <span className="font-medium">{selectedProductData.structure}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Draw Period:</span>
                <span className="font-medium">{selectedProductData.drawPeriod}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Repayment:</span>
                <span className="font-medium">{selectedProductData.repaymentPeriod}</span>
              </div>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>💡 Key Insight:</strong> {selectedProductData.description}
              </p>
            </div>
          </div>
        </div>

        {/* Market Comparison */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="text-2xl mr-2">⚖️</span>
            HELOC vs HELOAN Comparison
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-purple-600 flex items-center">
                💳 HELOC Average Rate
              </h3>
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-2xl font-bold text-purple-600">
                    {formatRate(marketData.helocAverage.current)}
                  </span>
                  <span className={`font-medium ${getChangeColor(marketData.helocAverage.change)}`}>
                    {formatChange(marketData.helocAverage.change)}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Range: {formatRate(marketData.helocAverage.range.min)} - {formatRate(marketData.helocAverage.range.max)}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-lg text-indigo-600 flex items-center">
                🏦 HELOAN Average Rate
              </h3>
              <div className="bg-indigo-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-2xl font-bold text-indigo-600">
                    {formatRate(marketData.heloanAverage.current)}
                  </span>
                  <span className={`font-medium ${getChangeColor(marketData.heloanAverage.change)}`}>
                    {formatChange(marketData.heloanAverage.change)}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Range: {formatRate(marketData.heloanAverage.range.min)} - {formatRate(marketData.heloanAverage.range.max)}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <div className="flex items-center mb-2">
              <span className="text-yellow-600 text-xl mr-2">📈</span>
              <span className="font-semibold text-yellow-800">Rate Spread Analysis</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">HELOC vs Prime:</span>
                <span className="font-medium ml-2">{formatRate(marketData.historicalSpreads.helocToPrime)}</span>
              </div>
              <div>
                <span className="text-gray-600">HELOAN vs Prime:</span>
                <span className="font-medium ml-2">{formatRate(marketData.historicalSpreads.heloanToPrime)}</span>
              </div>
              <div>
                <span className="text-gray-600">HELOC vs HELOAN:</span>
                <span className="font-medium ml-2">{formatRate(marketData.historicalSpreads.helocVsHeloan)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits and Considerations */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-green-600 mb-4 flex items-center">
              <span className="text-2xl mr-2">✅</span>
              {selectedProductData.name} Benefits
            </h3>
            <ul className="space-y-3">
              {selectedProductData.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-500 mr-2 mt-1">•</span>
                  <span className="text-gray-700">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-orange-600 mb-4 flex items-center">
              <span className="text-2xl mr-2">⚠️</span>
              Important Considerations
            </h3>
            <ul className="space-y-3">
              {selectedProductData.considerations.map((consideration, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-orange-500 mr-2 mt-1">•</span>
                  <span className="text-gray-700">{consideration}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Prime Rate Tracker */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <span className="text-2xl mr-2">📈</span>
            Prime Rate & Fed Funds Tracker
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-blue-600 font-medium mb-1">Federal Funds Rate</div>
              <div className="text-2xl font-bold text-blue-700">
                {formatRate(marketData.fedFunds.current)}
              </div>
              <div className={`text-sm ${getChangeColor(marketData.fedFunds.change)}`}>
                {formatChange(marketData.fedFunds.change)} since last meeting
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Last updated: {marketData.fedFunds.lastUpdate}
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-green-600 font-medium mb-1">Prime Rate</div>
              <div className="text-2xl font-bold text-green-700">
                {formatRate(marketData.primeRate.current)}
              </div>
              <div className={`text-sm ${getChangeColor(marketData.primeRate.change)}`}>
                {formatChange(marketData.primeRate.change)} since last change
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Last updated: {marketData.primeRate.lastUpdate}
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-purple-600 font-medium mb-1">Typical HELOC Rate</div>
              <div className="text-2xl font-bold text-purple-700">
                {formatRate(marketData.helocAverage.current)}
              </div>
              <div className="text-sm text-gray-600">
                Prime + {formatRate(marketData.historicalSpreads.helocToPrime)} margin
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Market average estimate
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">📚 Understanding the Relationship</h4>
            <p className="text-sm text-gray-700 leading-relaxed">
              The Prime Rate typically follows the Federal Funds Rate, usually set at Fed Funds + 3.00%. 
              HELOC rates are then priced as Prime Rate + a margin (typically 0.5% to 4.0% depending on creditworthiness and loan-to-value). 
              When the Fed raises rates, Prime Rate increases, directly affecting HELOC payments since most HELOCs have variable rates tied to Prime.
            </p>
          </div>
        </div>

        {/* Educational Footer */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl shadow-lg text-white p-6">
          <h3 className="text-xl font-bold mb-4 flex items-center">
            <span className="text-2xl mr-2">🎓</span>
            HELOC vs HELOAN: Making the Right Choice
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold mb-2">Choose HELOC if you:</h4>
              <ul className="text-sm space-y-1 opacity-90">
                <li>• Need flexible access to funds over time</li>
                <li>• Want to pay interest only on what you use</li>
                <li>• Can handle variable payment amounts</li>
                <li>• Are comfortable with rate risk</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-2">Choose HELOAN if you:</h4>
              <ul className="text-sm space-y-1 opacity-90">
                <li>• Need a specific lump sum amount</li>
                <li>• Want predictable monthly payments</li>
                <li>• Prefer protection from rate increases</li>
                <li>• Value payment certainty over flexibility</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HELOCRates;
